import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ProfileService, type Profile } from '../entities/Profile';
import { createVoicenterSDK } from '../utils/voicenterSDK';
import { ExtensionEventReasonEnum } from '@voicenter-team/events-sdk';
import { VoicenterCredentials, VoicenterCallEvent, VoicenterStatus } from '../types/voicenter';
import { useTranslation } from 'react-i18next';
import { useGoServerStore } from '../stores/goServerStore';
import { useProfileSettingsStore } from '../stores/profileSettingsStore';
import { useChecklistStore } from '../stores/checklistStore';
import { useActiveLeadStore } from '../stores/activeLeadStore';
import { useCrmCenterStore } from '../stores/crmCenterStore';

interface VoicenterContextType {
  status: VoicenterStatus;
  isConnected: boolean;
  isConnecting: boolean;
  lastCallEvent: VoicenterCallEvent | null;
  profile: Profile | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  simulateCallStart: () => void;
  simulateCallEnd: () => void;
}

const VoicenterContext = createContext<VoicenterContextType | undefined>(undefined);

export const useVoicenter = () => {
  const context = useContext(VoicenterContext);
  if (context === undefined) {
    throw new Error('useVoicenter must be used within a VoicenterProvider');
  }
  return context;
};

interface VoicenterProviderProps {
  children: ReactNode;
}

export const VoicenterProvider: React.FC<VoicenterProviderProps> = ({ children }) => {
  const { user, centerId: authCenterId, role } = useAuth();
  const selectedCrmCenter = useCrmCenterStore(s => s.selectedCenterId);
  // Super-admin uses the picker-selected center for CRM lookups; everyone else
  // is bound to their JWT center. Hook is called unconditionally above.
  const centerId = role === 'super_admin'
    ? (selectedCrmCenter ?? authCenterId ?? null)
    : authCenterId;
  const { t } = useTranslation();
  const [status, setStatus] = useState<VoicenterStatus>({ status: 'disconnected' });
  const [lastCallEvent, setLastCallEvent] = useState<VoicenterCallEvent | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const sdkRef = useRef<any>(null);
  const profileServiceRef = useRef(new ProfileService());
  const activeVoicenterCallIdRef = useRef<string | null>(null);
  // The SDK's ExtensionEvent listener is registered once during connect() and
  // would otherwise capture a stale centerId from the moment connect ran (often
  // before the backend JWT refresh populates it). Read from a ref instead so
  // ANSWER events always see the latest value.
  const centerIdRef = useRef<string | null>(centerId);
  useEffect(() => {
    centerIdRef.current = centerId;
  }, [centerId]);

  // Ensure the Go-server SDK is initialized before any Voicenter ANSWER event can fire.
  // Otherwise an event arriving before Dashboard mounts would hit the !sdk guard in
  // startSalesCall and the call would not start.
  useEffect(() => {
    if (!useGoServerStore.getState().sdk) {
      console.log('[VOICENTER] Pre-initializing Go-server SDK for early ANSWER events');
      useGoServerStore.getState().initializeSDK();
    }
  }, []);

  const updateStatus = useCallback((newStatus: Partial<VoicenterStatus>) => {
    setStatus(prev => ({ ...prev, ...newStatus }));
  }, []);

  const handleVoicenterEvent = useCallback((eventType: 'call.start' | 'call.end', data: any) => {
    const callEvent: VoicenterCallEvent = {
      type: eventType,
      data,
      timestamp: new Date(),
    };

    setLastCallEvent(callEvent);

    // Show browser notification
    const message = eventType === 'call.start'
      ? t('profile.voicenter.events.callStarted')
      : t('profile.voicenter.events.callEnded');

    if (Notification.permission === 'granted') {
      new Notification(message, {
        icon: eventType === 'call.start' ? '📞' : '📴',
        tag: 'voicenter-call-event',
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(message, {
            icon: eventType === 'call.start' ? '📞' : '📴',
            tag: 'voicenter-call-event',
          });
        }
      });
    }

    console.log('Voicenter Event:', callEvent);
  }, [t]);

  const handleVoicenterCallEvent = useCallback(async (reason: string, data: any) => {
    console.log('Voicenter ExtensionEvent received:', { reason, data });

    try {
      // ANSWER = call answered/accepted (NOT NEWCALL which is dial/ringing)
      if (reason === ExtensionEventReasonEnum.ANSWER) {
        console.log('[VOICENTER] Call answered - starting sales call');

        // The Voicenter payload arrives nested at varying depths depending on the SDK
        // version / transport. Our wrapper adds one extra envelope, and the SDK itself
        // nests one more level. Additionally, when the rep is being spied on by a
        // manager, calls[] contains *multiple* call objects (the spy session AND the
        // real customer call), but `currentCall` always points to the active customer
        // call — so prefer currentCall over calls[0] to avoid picking the spy session
        // (which has callstatus:"Spying", direction:"Answer", and the contact NAME in
        // the phone fields, not a number).
        const envelopes: any[] = [
          data?.data?.data?.data,
          data?.data?.data,
          data?.data,
          data,
        ].filter(e => e && typeof e === 'object');

        const isUsable = (c: any): boolean => {
          if (!c || typeof c !== 'object') return false;
          if (typeof c.callstatus === 'string' && c.callstatus.toLowerCase() === 'spying') return false;
          return true;
        };

        // 1) currentCall on any envelope wins.
        // 2) Else, the first non-Spying entry in calls[] on any envelope.
        let call0: any = null;
        for (const env of envelopes) {
          if (isUsable(env.currentCall)) { call0 = env.currentCall; break; }
        }
        if (!call0) {
          for (const env of envelopes) {
            const arr = Array.isArray(env.calls) ? env.calls : null;
            if (!arr) continue;
            const match = arr.find(isUsable);
            if (match) { call0 = match; break; }
          }
        }

        // Phone selection rules:
        //   • For Outgoing calls, the *customer* is the dialed party. Voicenter mirrors
        //     this into actualDialedNumber / callerphone / callername (all equal in tests).
        //     outgoingcallerphone is the REP's number — we must never use it as the lead phone.
        //   • For Incoming, callerphone holds the customer's number.
        //   • Any field whose value contains non-digit characters (letters/Hebrew) is
        //     a name, not a phone — reject it and try the next field.
        const looksLikePhone = (v: unknown): v is string => {
          if (typeof v !== 'string') return false;
          const digits = v.replace(/\D/g, '');
          return digits.length >= 7 && !/[A-Za-zא-ת]/.test(v);
        };
        const pickPhone = (...vals: unknown[]): string | undefined => {
          for (const v of vals) if (looksLikePhone(v)) return v as string;
          return undefined;
        };
        const direction = (call0?.direction ?? '').toLowerCase();
        const isOutgoing = direction === 'outgoing';
        const phone = isOutgoing
          ? pickPhone(call0?.actualDialedNumber, call0?.callerphone, call0?.callername)
          : pickPhone(call0?.callerphone, call0?.originalCallerID, call0?.number, call0?.actualDialedNumber);
        // Top-level fallbacks (present on HANGUP and some ANSWER variants).
        // Pass through the same digit-only sieve so we never pick up a name.
        const fallbackPhone = pickPhone(
          data?.data?.data?.data?.callerID,
          data?.data?.data?.callerID,
          data?.data?.callerID,
          data?.callerID,
        );
        const finalPhone = phone ?? fallbackPhone;

        const ivrFallback =
          data?.data?.data?.data?.ivruniqueid ??
          data?.data?.data?.ivruniqueid ??
          data?.data?.ivruniqueid;
        const callId = call0?.ivrid
          ? String(call0.ivrid)
          : call0?.id
            ? String(call0.id)
            : ivrFallback
              ? String(ivrFallback)
              : undefined;
        activeVoicenterCallIdRef.current = callId ?? null;
        console.log(
          `%c[VOICENTER] 📞 CALL STARTED — phone: ${finalPhone ?? '(none)'} | direction: ${direction || '?'} | callId: ${callId ?? '(none)'}`,
          'color:#10b981;font-weight:bold;'
        );
        const currentCenterId = centerIdRef.current;
        console.log('[VOICENTER] CRM lookup extraction:', {
          phone: finalPhone,
          direction,
          callId,
          hasCenterId: !!currentCenterId,
          call0Keys: call0 ? Object.keys(call0) : null,
          actualDialedNumber: call0?.actualDialedNumber,
          callername: call0?.callername,
          callerphone: call0?.callerphone,
          outgoingcallerphone: call0?.outgoingcallerphone,
          originalCallerID: call0?.originalCallerID,
          number: call0?.number,
          fallbackPhone,
        });
        if (finalPhone && currentCenterId) {
          console.log(`[VOICENTER] 🔎 Starting CRM retry-loop for phone=${finalPhone}, centerId=${currentCenterId}`);
          useActiveLeadStore.getState().beginRetryLoop(String(finalPhone), callId);
        } else {
          console.warn('[VOICENTER] ⚠️ Skipping CRM lookup — missing phone or centerId', {
            phone: finalPhone,
            centerId: currentCenterId,
          });
        }

        // Read latest values directly to avoid stale closures in long-lived listeners
        const storeState = useProfileSettingsStore.getState();
        const freshProfile = storeState.profile;
        const goState = useGoServerStore.getState();
        const { centerType } = useChecklistStore.getState();

        const salesmanMicId = storeState.defaultDeviceIdSalesman || freshProfile?.default_device_id_salesman;
        const clientMicId = storeState.defaultDeviceIdClient || freshProfile?.default_device_id_client;

        if (!salesmanMicId || !clientMicId) {
          console.error('[VOICENTER] Cannot start call on ANSWER: missing device selection', {
            salesmanMicId,
            clientMicId,
            profileExists: !!freshProfile
          });
          return;
        }

        console.log('[VOICENTER] startSalesCall params:', {
          salesmanMicId,
          clientMicId,
          userId: user?.id,
          centerType
        });

        // startSalesCall has its own concurrency guard (salesLoading / isActive),
        // so it is safe to invoke unconditionally here - no caller-side gating needed.
        await goState.startSalesCall(salesmanMicId, clientMicId, user?.id, centerType);

        // Spec 2 §6.5: stamp crm_leads.last_call_at when the call actually starts and
        // we have an active CRM lead. Fire-and-forget; RLS handles authorization.
        const activeLead = useActiveLeadStore.getState().lead;
        if (activeLead?.id) {
          import('../utils/supabase').then(({ default: supabase }) => {
            supabase.from('crm_leads')
              .update({ last_call_at: new Date().toISOString() })
              .eq('id', activeLead.id)
              .then((r: any) => {
                if (r?.error) console.warn('[VOICENTER] last_call_at update failed:', r.error.message);
              });
          });
        }

        handleVoicenterEvent('call.start', data);
      } else if (reason === ExtensionEventReasonEnum.NEWCALL) {
        console.log('[VOICENTER] New call initiated (ringing) - waiting for answer');
      } else if (reason === ExtensionEventReasonEnum.HANGUP) {
        // Voicenter HANGUP often arrives with calls=[] and the call object on currentCall.
        // Mirror the deep extraction used in ANSWER so we don't silently fail to end the call.
        const hangupCall =
          data?.data?.data?.data?.calls?.[0] ??
          data?.data?.data?.data?.currentCall ??
          data?.data?.data?.calls?.[0] ??
          data?.data?.data?.currentCall ??
          data?.data?.calls?.[0] ??
          data?.data?.currentCall ??
          null;
        const hangupIvr =
          data?.data?.data?.data?.ivruniqueid ??
          data?.data?.data?.ivruniqueid ??
          data?.data?.ivruniqueid ??
          data?.data?.callId;
        const hangupCallId = hangupCall?.ivrid
          ? String(hangupCall.ivrid)
          : hangupCall?.id
            ? String(hangupCall.id)
            : hangupIvr
              ? String(hangupIvr)
              : undefined;
        const activeId = activeVoicenterCallIdRef.current;

        // Fail-open: if either id is missing, treat as a match so we never get
        // stuck unable to end the call due to a malformed payload.
        const isForActiveCall = !hangupCallId || !activeId || hangupCallId === activeId;

        if (!isForActiveCall) {
          console.log('[VOICENTER] HANGUP ignored - not for active call', { hangupCallId, activeId });
          return;
        }

        console.log('[VOICENTER] Call ended - stopping sales call');
        const goState = useGoServerStore.getState();
        if (goState.salesCallStatus.isActive) {
          await goState.stopSalesCall();
        } else {
          console.warn('[VOICENTER] HANGUP received but no active sales call to stop');
        }
        useActiveLeadStore.getState().stopRetryLoop();
        activeVoicenterCallIdRef.current = null;
        handleVoicenterEvent('call.end', data);
      } else if (reason === ExtensionEventReasonEnum.USER_STATUS_UPDATE) {
        console.log('[VOICENTER] User status updated:', data);
      } else {
        console.log('[VOICENTER] Unhandled extension event reason:', reason);
      }
    } catch (error) {
      console.error('[VOICENTER] Error handling call event:', error);
    }
  }, [user?.id, handleVoicenterEvent]);

  const connect = useCallback(async () => {
    if (!user?.id) {
      updateStatus({ status: 'error', lastError: t('profile.voicenter.errors.noUser') });
      return;
    }

    try {
      updateStatus({ status: 'connecting' });

      // Fetch user credentials from Supabase
      const userProfile = await profileServiceRef.current.getByUserId(user.id);

      if (!userProfile?.voicenter_login || !userProfile?.voicecenter_password) {
        updateStatus({
          status: 'error',
          lastError: t('profile.voicenter.errors.noCredentials')
        });
        return;
      }

      // Store profile for use in event handlers
      setProfile(userProfile);

      const credentials: VoicenterCredentials = {
        username: userProfile.voicenter_login,
        password: userProfile.voicecenter_password,
      };

      // Initialize SDK
      const sdk = createVoicenterSDK(credentials);
      sdkRef.current = sdk;

      // Subscribe to events according to Voicenter documentation.
      // Our wrapper (voicenterSDK.handleCallEvent) re-wraps the original SDK payload as:
      //   { type: 'call.start'|'call.end', data: <originalSDKResponse>, timestamp }
      // Where <originalSDKResponse> = { name: 'ExtensionEvent', data: { data: {calls...}, reason, callerID } }
      // So `reason` lives at response.data.data.reason — NOT response.data.reason.
      // We check both depths so a future un-wrap doesn't silently break the flow.
      sdk.on('ExtensionEvent', (response: any) => {
        console.log('[VOICENTER] sdk.on(ExtensionEvent) fired:', response);
        const reason =
          response?.data?.data?.reason ??
          response?.data?.reason ??
          response?.reason;
        if (!reason) {
          console.warn('[VOICENTER] no reason found in payload — skipping', response);
          return;
        }
        handleVoicenterCallEvent(reason, response);
      });

      // Attempt login
      await sdk.login();

      updateStatus({
        status: 'connected',
        lastConnected: new Date(),
        lastError: undefined
      });

      console.log('Voicenter SDK connected successfully');

    } catch (error: any) {
      console.error('Voicenter connection failed:', error);
      updateStatus({
        status: 'error',
        lastError: error.message || t('profile.voicenter.errors.connectionFailed')
      });

      // Show browser notification for error
      if (Notification.permission === 'granted') {
        new Notification(t('profile.voicenter.events.connectionFailed'), {
          icon: '⚠️',
          tag: 'voicenter-error',
        });
      }
    }
  }, [user?.id, handleVoicenterEvent, updateStatus, t]);

  const disconnect = useCallback(() => {
    console.log('Manual disconnect called');
    if (sdkRef.current) {
      try {
        sdkRef.current.destroy();
        console.log('SDK destroyed successfully on manual disconnect');
      } catch (error) {
        console.error('Error destroying SDK on manual disconnect:', error);
      }
      sdkRef.current = null;
    }
    updateStatus({ status: 'disconnected', lastError: undefined });
    console.log('Voicenter SDK disconnected');
  }, [updateStatus]);

  const simulateCallStart = useCallback(() => {
    if (sdkRef.current) {
      sdkRef.current.simulateCallStart();
    }
  }, []);

  const simulateCallEnd = useCallback(() => {
    if (sdkRef.current) {
      sdkRef.current.simulateCallEnd();
    }
  }, []);

  // Auto-connect when user is available.
  // Depends ONLY on user?.id so this fires once per user. `connect` mutates
  // `status.status` (disconnected → connecting → connected), so including
  // status in deps caused this effect — and its Supabase profile fetch — to
  // re-run on every status transition. Use refs for current connect/status.
  const connectRef = useRef(connect);
  connectRef.current = connect;
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const fetchedProfile = await profileServiceRef.current.getByUserId(user.id);
        if (cancelled || !fetchedProfile) return;
        setProfile(fetchedProfile);
        if (
          fetchedProfile.voicenter_login &&
          fetchedProfile.voicecenter_password &&
          statusRef.current.status === 'disconnected'
        ) {
          await connectRef.current();
        }
      } catch (error) {
        console.log('Error fetching profile or connecting:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Listen to Backend Events (bridging SocketService events to VoicenterContext)
  useEffect(() => {
    const handleBackendCallStart = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('VoicenterContext received BackendCallStarting:', customEvent.detail);

      // Simulate/Trigger standard call start logic
      // Transform backend event data to match what handleVoicenterCallEvent expects if possible, 
      // or just directly trigger the sales call logic.

      const { targetNumber, provider, callId } = customEvent.detail;

      // Treat backend call-start as an ANSWER event to auto-start the sales flow
      const mappedData = {
        data: {
          reason: ExtensionEventReasonEnum.ANSWER,
          calls: [{
            id: callId,
            number: targetNumber,
            status: 'connected',
            provider: provider
          }]
        }
      };

      handleVoicenterCallEvent(ExtensionEventReasonEnum.ANSWER, mappedData);
    };

    const handleBackendCallEnd = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('VoicenterContext received BackendCallEnded:', customEvent.detail);

      const mappedData = {
        data: {
          reason: ExtensionEventReasonEnum.HANGUP,
          callId: customEvent.detail.callId
        }
      };

      handleVoicenterCallEvent(ExtensionEventReasonEnum.HANGUP, mappedData);
    };

    window.addEventListener('BackendCallStarting', handleBackendCallStart);
    window.addEventListener('BackendCallEnded', handleBackendCallEnd);

    return () => {
      window.removeEventListener('BackendCallStarting', handleBackendCallStart);
      window.removeEventListener('BackendCallEnded', handleBackendCallEnd);
    };
  }, [handleVoicenterCallEvent]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('VoicenterProvider unmounting - cleaning up SDK');
      if (sdkRef.current) {
        try {
          sdkRef.current.destroy();
          console.log('SDK destroyed successfully on unmount');
        } catch (error) {
          console.error('Error destroying SDK on unmount:', error);
        }
        sdkRef.current = null;
      }
      useActiveLeadStore.getState().stopRetryLoop();
    };
  }, []);

  const contextValue: VoicenterContextType = {
    status,
    isConnected: status.status === 'connected',
    isConnecting: status.status === 'connecting',
    lastCallEvent,
    profile,
    connect,
    disconnect,
    simulateCallStart,
    simulateCallEnd,
  };

  return (
    <VoicenterContext.Provider value={contextValue}>
      {children}
    </VoicenterContext.Provider>
  );
};
